import { inject, Injectable } from '@angular/core';
import { SharedUserApi, type UpdateBasicUserFieldsInput } from '@reconcept-platform/shared/data-access';
import {
  type CreateUserInput,
  type DashboardRoleAccessLevelEnum,
  WelcomeMailSourceEnum,
  type WorkspaceRoleAccessLevelEnum,
  type WorkspaceUserItemFragment,
} from '@reconcept-platform/shared/types';
import { map } from 'rxjs/operators';
import { UserApi } from '../../api/user/user.api';

type WorkspaceDashboardRoleNode =
  | { title?: string; dashboardRoleAccessLevel: DashboardRoleAccessLevelEnum; dashboard?: { id: string } }
  | { title?: string; workspaceRoleAccessLevel: WorkspaceRoleAccessLevelEnum; workspace?: { id: string } };

export interface WorkspaceDashboardRolesConnection {
  edges?: { node?: WorkspaceDashboardRoleNode }[];
}

export function mapToUserView(
  user?: WorkspaceUserItemFragment & { workspaceDashboardRoles?: WorkspaceDashboardRolesConnection },
): UserView {
  // @ts-expect-error TS2322 type not assignable
  return {
    ...user,
    roles: user?.workspaceDashboardRoles ? mapToWorkspaceDashboardRoles(user.workspaceDashboardRoles) : undefined,
  };
}

export function mapToWorkspaceDashboardRoles(workspaceDashboardRoles: WorkspaceDashboardRolesConnection) {
  return (workspaceDashboardRoles.edges ?? []).flatMap((edge) =>
    edge.node ? [mapToWorkspaceDashboardRole(edge.node)] : [],
  );
}

export function mapToWorkspaceDashboardRole(workspaceDashboardRole: WorkspaceDashboardRoleNode): UserViewRole {
  if ('dashboardRoleAccessLevel' in workspaceDashboardRole) {
    return {
      title: workspaceDashboardRole.title,
      accessLevel: workspaceDashboardRole.dashboardRoleAccessLevel,
      dashboard: workspaceDashboardRole.dashboard,
    };
  }
  return {
    title: workspaceDashboardRole.title,
    accessLevel: workspaceDashboardRole.workspaceRoleAccessLevel,
    workspace: workspaceDashboardRole.workspace,
  };
}

export interface UserViewRole {
  title?: string;
  accessLevel: WorkspaceRoleAccessLevelEnum | DashboardRoleAccessLevelEnum;
  workspace?: { id: string };
  dashboard?: { id: string };
}

export interface UserView extends WorkspaceUserItemFragment {
  roles?: UserViewRole[];
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private userApi = inject(UserApi);
  private sharedUserApi = inject(SharedUserApi);

  createUser(createUserInput: CreateUserInput) {
    return this.sharedUserApi.createUser(createUserInput);
  }

  sendWelcomeEmailToEmailAddress(email: string) {
    return this.sharedUserApi.sendWelcomeEmailToEmailAddress(email, WelcomeMailSourceEnum.WORKSPACE_DASHBOARD);
  }

  updateUser(updateUserInput: UpdateBasicUserFieldsInput) {
    return this.sharedUserApi.updateUser(updateUserInput);
  }

  deleteUser(userId: string) {
    // @ts-expect-error TS2345 argument not assignable
    return this.userApi.deleteUser(userId).pipe(map((user) => mapToUserView(user)));
  }

  getUserDetail(userId: string) {
    return this.userApi.getUserDetail(userId);
  }

  findUserByEmail(email: string) {
    // @ts-expect-error TS2345 argument not assignable
    return this.userApi.findUserByEmail(email).pipe(map((user) => (user ? mapToUserView(user) : undefined)));
  }

  mapToUserView(user: Parameters<typeof mapToUserView>[0]) {
    return mapToUserView(user);
  }

  mapToWorkspaceDashboardRoles(workspaceDashboardRoles: WorkspaceDashboardRolesConnection) {
    return mapToWorkspaceDashboardRoles(workspaceDashboardRoles);
  }

  watchAvatar(id: string) {
    return this.userApi.watchAvatar(id);
  }
}
